import type { Lang } from '../types';

export interface ApartmentsText {
  pageTitle: string;
  addApartment: string;
  filterTitle: string;
  filterName: string;
  filterContact: string;
  filterAllContacts: string;
  filterNoContact: string;
  filterAll: string;
  filterActive: string;
  filterInactive: string;
  searchByName: string;
  formInternalName: string;
  formDisplayName: string;
  formAddress: string;
  formCapacity: string;
  formContact: string;
  formNoContact: string;
  formDescription: string;
  formActive: string;
  createBtn: string;
  loading: string;
  showing: (shown: number, total: number) => string;
  pageInfo: (page: number, total: number) => string;
  sortBy: string;
  sortId: string;
  sortName: string;
  itemsPerPage: string;
  tableId: string;
  tableName: string;
  tableAddress: string;
  tableCapacity: string;
  tableContact: string;
  tableStatus: string;
  tableCalendarSync: string;
  connectCalendarCta: string;
  tableActions: string;
  contactYou: string;
  statusActive: string;
  statusInactive: string;
  calendarSyncOk: string;
  calendarSyncError: string;
  calendarSyncErrorHint: string;
  reservations: string;
  btnEdit: string;
  limitTitle: string;
  limitBody: (limit: number) => string;
  limitMessageLabel: string;
  limitMessageOptional: string;
  limitMessagePlaceholder: string;
  limitRequestBtn: string;
  limitSentTitle: string;
  limitSentBody: string;
  apartmentCreated: string;
  emptyTitle: string;
  emptyBody: string;
  emptyNoResults: string;
}

export const apartmentsText: Record<Lang, ApartmentsText> = {
  cs: {
    pageTitle: 'Apartmány',
    addApartment: 'Přidat apartmán',
    filterTitle: 'Filtrovat apartmány',
    filterName: 'Název:',
    filterContact: 'Kontakt:',
    filterAllContacts: 'Všechny kontakty',
    filterNoContact: 'Bez kontaktu (spravuji sám)',
    filterAll: 'Vše',
    filterActive: 'Aktivní',
    filterInactive: 'Neaktivní',
    searchByName: 'Hledat podle názvu…',
    formInternalName: 'Interní název',
    formDisplayName: 'Zobrazovaný název',
    formAddress: 'Adresa',
    formCapacity: 'Kapacita',
    formContact: 'Kontakt (volitelné)',
    formNoContact: '-- Bez přiřazeného kontaktu --',
    formDescription: 'Popis',
    formActive: 'Aktivní',
    createBtn: 'Vytvořit',
    loading: 'Načítám…',
    showing: (shown, total) => `Zobrazuji ${shown} z ${total} apartmánů`,
    pageInfo: (page, total) => `(Stránka ${page} z ${total})`,
    sortBy: 'Seřadit podle:',
    sortId: 'ID',
    sortName: 'Název',
    itemsPerPage: 'Položek na stránku:',
    tableId: 'ID',
    tableName: 'Název',
    tableAddress: 'Adresa',
    tableCapacity: 'Kapacita',
    tableContact: 'Kontakt',
    tableStatus: 'Stav',
    tableCalendarSync: 'Sync kalendáře',
    connectCalendarCta: 'Připojit kalendář →',
    tableActions: 'Akce',
    contactYou: 'Vy',
    statusActive: 'Aktivní',
    statusInactive: 'Neaktivní',
    calendarSyncOk: 'OK',
    calendarSyncError: 'Zkontrolovat',
    calendarSyncErrorHint: 'Synchronizace kalendáře nefunguje — otevřete záložku Kalendář u apartmánu a zjistěte proč.',
    reservations: 'Rezervace',
    btnEdit: 'Upravit',
    limitTitle: 'Dosažen limit apartmánů',
    limitBody: (limit) => `Dosáhli jste limitu ${limit} apartmánů. Pro přidání dalších potřebujete souhlas správce.`,
    limitMessageLabel: 'Zpráva správci',
    limitMessageOptional: '(volitelné)',
    limitMessagePlaceholder: 'Stručně vysvětlete, proč potřebujete více apartmánů…',
    limitRequestBtn: 'Požádat o navýšení limitu',
    limitSentTitle: 'Žádost odeslána',
    limitSentBody: 'Správce vaši žádost posoudí a zvýší limit, pokud bude schválena.',
    apartmentCreated: 'Apartmán vytvořen',
    emptyTitle: 'Zatím žádný apartmán',
    emptyBody: 'Začněte přidáním prvního apartmánu. Po přidání budete moci přijímat rezervace, synchronizovat kalendáře a spravovat hosty.',
    emptyNoResults: 'Žádné apartmány neodpovídají zadaným filtrům.',
  },
  en: {
    pageTitle: 'Apartments',
    addApartment: 'Add Apartment',
    filterTitle: 'Filter Apartments',
    filterName: 'Name:',
    filterContact: 'Contact:',
    filterAllContacts: 'All Contacts',
    filterNoContact: 'No Contact (I manage)',
    filterAll: 'All',
    filterActive: 'Active',
    filterInactive: 'Inactive',
    searchByName: 'Search by name...',
    formInternalName: 'Internal Name',
    formDisplayName: 'Apartment Display Name',
    formAddress: 'Address',
    formCapacity: 'Capacity',
    formContact: 'Contact (optional)',
    formNoContact: '-- No contact assigned --',
    formDescription: 'Description',
    formActive: 'Active',
    createBtn: 'Create',
    loading: 'Loading...',
    showing: (shown, total) => `Showing ${shown} of ${total} apartments`,
    pageInfo: (page, total) => `(Page ${page} of ${total})`,
    sortBy: 'Sort by:',
    sortId: 'ID',
    sortName: 'Name',
    itemsPerPage: 'Items per page:',
    tableId: 'ID',
    tableName: 'Name',
    tableAddress: 'Address',
    tableCapacity: 'Capacity',
    tableContact: 'Contact',
    tableStatus: 'Status',
    tableCalendarSync: 'Calendar sync',
    connectCalendarCta: 'Connect calendar →',
    tableActions: 'Actions',
    contactYou: 'You',
    statusActive: 'Active',
    statusInactive: 'Inactive',
    calendarSyncOk: 'OK',
    calendarSyncError: 'Check apartment',
    calendarSyncErrorHint: "Calendar sync isn't working — open the apartment's Calendar tab to see what's wrong.",
    reservations: 'Reservations',
    btnEdit: 'Edit',
    limitTitle: 'Apartment limit reached',
    limitBody: (limit) => `You have reached your current apartment limit (${limit}). To add more apartments, you need approval from the admin.`,
    limitMessageLabel: 'Message to admin',
    limitMessageOptional: '(optional)',
    limitMessagePlaceholder: 'Briefly explain why you need more apartments...',
    limitRequestBtn: 'Request limit increase',
    limitSentTitle: 'Request submitted',
    limitSentBody: 'The admin will review your request and increase your limit if approved.',
    apartmentCreated: 'Apartment created',
    emptyTitle: 'No apartments yet',
    emptyBody: 'Start by adding your first apartment. Once added, you can accept reservations, sync calendars, and manage guests.',
    emptyNoResults: 'No apartments match the current filters.',
  },
  vi: {
    pageTitle: 'Căn hộ',
    addApartment: 'Thêm căn hộ',
    filterTitle: 'Lọc căn hộ',
    filterName: 'Tên:',
    filterContact: 'Liên hệ:',
    filterAllContacts: 'Tất cả liên hệ',
    filterNoContact: 'Không có liên hệ (tôi quản lý)',
    filterAll: 'Tất cả',
    filterActive: 'Hoạt động',
    filterInactive: 'Không hoạt động',
    searchByName: 'Tìm theo tên…',
    formInternalName: 'Tên nội bộ',
    formDisplayName: 'Tên hiển thị',
    formAddress: 'Địa chỉ',
    formCapacity: 'Sức chứa',
    formContact: 'Liên hệ (tùy chọn)',
    formNoContact: '-- Không có liên hệ được chỉ định --',
    formDescription: 'Mô tả',
    formActive: 'Hoạt động',
    createBtn: 'Tạo',
    loading: 'Đang tải…',
    showing: (shown, total) => `Hiển thị ${shown} / ${total} căn hộ`,
    pageInfo: (page, total) => `(Trang ${page} / ${total})`,
    sortBy: 'Sắp xếp theo:',
    sortId: 'ID',
    sortName: 'Tên',
    itemsPerPage: 'Mỗi trang:',
    tableId: 'ID',
    tableName: 'Tên',
    tableAddress: 'Địa chỉ',
    tableCapacity: 'Sức chứa',
    tableContact: 'Liên hệ',
    tableStatus: 'Trạng thái',
    tableCalendarSync: 'Đồng bộ lịch',
    connectCalendarCta: 'Kết nối lịch →',
    tableActions: 'Thao tác',
    contactYou: 'Bạn',
    statusActive: 'Hoạt động',
    statusInactive: 'Không hoạt động',
    calendarSyncOk: 'OK',
    calendarSyncError: 'Cần kiểm tra',
    calendarSyncErrorHint: 'Đồng bộ lịch không hoạt động — mở tab Lịch của căn hộ để xem vấn đề.',
    reservations: 'Lịch đặt',
    btnEdit: 'Sửa',
    limitTitle: 'Đã đạt giới hạn căn hộ',
    limitBody: (limit) => `Bạn đã đạt giới hạn ${limit} căn hộ. Để thêm, bạn cần được quản trị viên phê duyệt.`,
    limitMessageLabel: 'Tin nhắn cho quản trị viên',
    limitMessageOptional: '(tùy chọn)',
    limitMessagePlaceholder: 'Giải thích ngắn gọn tại sao bạn cần thêm căn hộ…',
    limitRequestBtn: 'Yêu cầu tăng giới hạn',
    limitSentTitle: 'Yêu cầu đã gửi',
    limitSentBody: 'Quản trị viên sẽ xem xét và tăng giới hạn nếu được phê duyệt.',
    apartmentCreated: 'Đã tạo căn hộ',
    emptyTitle: 'Chưa có căn hộ nào',
    emptyBody: 'Bắt đầu bằng cách thêm căn hộ đầu tiên. Sau khi thêm, bạn có thể nhận lịch đặt, đồng bộ lịch và quản lý khách.',
    emptyNoResults: 'Không có căn hộ nào phù hợp với bộ lọc hiện tại.',
  },
  uk: {
    pageTitle: 'Апартаменти',
    addApartment: 'Додати апартамент',
    filterTitle: 'Фільтр апартаментів',
    filterName: 'Назва:',
    filterContact: 'Контакт:',
    filterAllContacts: 'Всі контакти',
    filterNoContact: 'Без контакту (я керую)',
    filterAll: 'Всі',
    filterActive: 'Активні',
    filterInactive: 'Неактивні',
    searchByName: 'Пошук за назвою…',
    formInternalName: 'Внутрішня назва',
    formDisplayName: 'Відображувана назва',
    formAddress: 'Адреса',
    formCapacity: 'Місткість',
    formContact: 'Контакт (необов\'язково)',
    formNoContact: '-- Без призначеного контакту --',
    formDescription: 'Опис',
    formActive: 'Активний',
    createBtn: 'Створити',
    loading: 'Завантаження…',
    showing: (shown, total) => `Показано ${shown} з ${total} апартаментів`,
    pageInfo: (page, total) => `(Сторінка ${page} з ${total})`,
    sortBy: 'Сортувати за:',
    sortId: 'ID',
    sortName: 'Назва',
    itemsPerPage: 'На сторінку:',
    tableId: 'ID',
    tableName: 'Назва',
    tableAddress: 'Адреса',
    tableCapacity: 'Місткість',
    tableContact: 'Контакт',
    tableStatus: 'Статус',
    tableCalendarSync: 'Синхронізація календаря',
    connectCalendarCta: 'Підключити календар →',
    tableActions: 'Дії',
    contactYou: 'Ви',
    statusActive: 'Активний',
    statusInactive: 'Неактивний',
    calendarSyncOk: 'OK',
    calendarSyncError: 'Перевірити',
    calendarSyncErrorHint: 'Синхронізація календаря не працює — відкрийте вкладку Календар апартаменту, щоб дізнатися причину.',
    reservations: 'Бронювання',
    btnEdit: 'Редагувати',
    limitTitle: 'Досягнуто ліміт апартаментів',
    limitBody: (limit) => `Ви досягли ліміту ${limit} апартаментів. Для додавання потрібен дозвіл адміністратора.`,
    limitMessageLabel: 'Повідомлення адміністратору',
    limitMessageOptional: '(необов\'язково)',
    limitMessagePlaceholder: 'Стисло поясніть, чому вам потрібно більше апартаментів…',
    limitRequestBtn: 'Запросити збільшення ліміту',
    limitSentTitle: 'Запит надіслано',
    limitSentBody: 'Адміністратор розгляне запит і збільшить ліміт за потреби.',
    apartmentCreated: 'Апартамент створено',
    emptyTitle: 'Апартаментів ще немає',
    emptyBody: 'Почніть із додавання першого апартаменту. Після цього ви зможете приймати бронювання, синхронізувати календарі та керувати гостями.',
    emptyNoResults: 'Жоден апартамент не відповідає поточним фільтрам.',
  },
};
